import React, { useEffect, useState } from 'react';
import { Alert, Button, Empty, Input, Modal, Table, Tag } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { apiToken } from '../../config/ApiService';
import type { ResourceImportProps } from './CopyDealResourcesModal';
import BasePriceVersionWarning from './BasePriceVersionWarning';
import { CopyResource, copyResourceRows, matchingRate } from '../services/copyResources';

interface Employee extends CopyResource {
    employee_id: string; email: string; mapped: boolean;
}

export default function EmployeeResourcesModal({ start, end, duration, rates, firstKey, onApply, onClose, basePriceVersionId, pricingVersions }: ResourceImportProps) {
    const [query, setQuery] = useState('');
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [selected, setSelected] = useState<Employee[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [error, setError] = useState('');
    const [retry, setRetry] = useState(0);
    useEffect(() => {
        let active = true;
        setEmployees([]); setHasMore(false); setError('');
        const search = query.trim();
        setLoading(search.length >= 2);
        if (search.length < 2) return;
        const timer = setTimeout(async () => {
            try {
                const { data } = await apiToken.get<{ employees: Employee[]; has_more: boolean }>('/pricing_tool/employee_resources', { params: { q: search } });
                if (active) { setEmployees(data.employees); setHasMore(data.has_more); }
            } catch {
                if (active) setError('Unable to search employees. Please try again.');
            } finally { if (active) setLoading(false); }
        }, 300);
        return () => { active = false; clearTimeout(timer); };
    }, [query, retry]);
    const available = (employee: Employee) => employee.mapped && matchingRate(employee, rates) >= 0;
    const apply = () => {
        if (!selected.length || selected.some(employee => !available(employee))) return;
        const rows = selected.map(employee => ({ ...employee,
            name: employee.email ? `${employee.name} (${employee.email})` : employee.name,
            start_date: start.format('YYYY-MM-DD'), end_date: end.format('YYYY-MM-DD'), allocation: 100, comment: '' }));
        onApply(copyResourceRows(rows, rates, null, start, end, duration, firstKey), false);
        onClose();
    };
    return <Modal open title={<>Select resources<BasePriceVersionWarning basePriceVersionId={basePriceVersionId} pricingVersions={pricingVersions} /></>} width={1050} onCancel={onClose} onOk={apply}
        okText={selected.length ? `Add ${selected.length} resource${selected.length === 1 ? '' : 's'}` : 'Add resources'}
        okButtonProps={{ disabled: !selected.length || selected.some(employee => !available(employee)) }}>
        <p>Select employees to add resource rows with their role details and rates from the current pricing catalog.</p>
        <Input autoFocus allowClear maxLength={100} prefix={<SearchOutlined />} aria-label="Search employees by name or email"
            placeholder="Search employees by name or email (at least 2 characters)" value={query}
            onChange={event => setQuery(event.target.value)} />
        {error && <Alert style={{ marginTop: 12 }} type="error" showIcon message={error}
            action={<Button size="small" onClick={() => setRetry(value => value + 1)}>Retry</Button>} />}
        {hasMore && <Alert style={{ marginTop: 12 }} type="info" showIcon message="Showing the first 50 matches. Refine your search to find more employees." />}
        <Table<Employee> style={{ marginTop: 16 }} size="small" rowKey="employee_id" loading={loading}
            dataSource={employees} pagination={false} scroll={{ y: 320, x: 850 }}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={query.trim().length < 2
                ? 'Enter a name or email to find employees' : error ? 'Search unavailable' : 'No eligible employees found. Try another name or email.'} /> }}
            rowSelection={{ selectedRowKeys: selected.map(employee => employee.employee_id), preserveSelectedRowKeys: true,
                onChange: (_, rows) => setSelected(rows), getCheckboxProps: employee => ({ disabled: !available(employee) }) }}
            columns={[
                { title: 'Employee', dataIndex: 'name', width: 230, render: (_, employee) => <><strong>{employee.name}</strong><div style={{ color: '#666' }}>{employee.email}</div></> },
                { title: 'Type', dataIndex: 'resource_type', width: 90 },
                { title: 'Region', dataIndex: 'region', width: 80 },
                { title: 'Service line', dataIndex: 'service_line' },
                { title: 'Function', dataIndex: 'function' },
                { title: 'Level', dataIndex: 'career_level', width: 70 },
                { title: 'Pricing', width: 150, render: (_, employee) => <Tag color={available(employee) ? 'success' : 'warning'}>
                    {available(employee) ? 'Rate available' : employee.mapped ? 'No catalog rate' : 'Role not mapped'}</Tag> },
            ]} />
        {!!selected.length && <div style={{ marginTop: 12 }}>
            <div style={{ marginBottom: 8 }}>{selected.length} selected · selections stay when you search again</div>
            {selected.map(employee => <Tag key={employee.employee_id} closable onClose={() => setSelected(rows => rows.filter(row => row.employee_id !== employee.employee_id))}>{employee.name}</Tag>)}
            <Button type="link" size="small" onClick={() => setSelected([])}>Clear selection</Button>
        </div>}
        <p style={{ marginTop: 16, color: '#666' }}>Adds to your existing resources with 100% allocation for the current project dates. Review allocations before saving. Employees without a matching catalog rate cannot be selected.</p>
    </Modal>;
}
